import { AuditChange, NormalizedAuditEntry } from "../Data/ApiTypes";
import { toLocalDateTime } from "../Data/Date";
import { formatSubscriptionStatus } from "../Data/Subscription";

export function buildAuditChanges(entries) {
  const sorted = [...entries].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const result: NormalizedAuditEntry[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const previous = sorted[i - 1];

    const changes: AuditChange[] = [];

    const isIntegration = current.eventName === "UnrollIntegrationEvent" || current.eventName === "EnrollIntegrationEvent";

    const fields = Object.keys(current.newValues || {});
    for (const field of fields) {
      if (isHiddenAuditField(field)) continue;

      const newValue = current.newValues[field];

      let oldValue = null;

      oldValue = previous?.newValues[field] ?? current.oldValues[field];

      const isDifferent = JSON.stringify(oldValue) !== JSON.stringify(newValue);

      //Integration events do not have different values, but we need to show them
      if (isDifferent ||  isIntegration) {
        if (!isEmptyObject(newValue) && newValue !== null) {
          changes.push({
            field,
            oldValue,
            newValue,
          });
        }
      }
    }

    result.push({
      ...current,
      changes,
    });
  }

  return result.reverse();
}

function isEmptyObject(obj: any) {
  return (
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0
  );
}

export function formatAllEvents(auditEntities) {
  if (!Array.isArray(auditEntities)) return [];

  return auditEntities
    .map((e) => {
      const oldObj = safeJson(e.oldValues) ?? {};
      const newObj = safeJson(e.newValues) ?? {};

      normalizeGroupChangeFields(oldObj, newObj, e.eventName);

      const fields = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

      const changes = [...fields]
        .filter((field) => !isHiddenAuditField(field))
        .map((field) => ({
        field,
        oldValue: oldObj[field] ?? null,
        newValue: newObj[field] ?? null,
      }));

      return {
        entityType: e.entityType,
        entityId: e.entityId,
        changeType: e.changeType,
        eventName: e.eventName ?? "",
        changedBy: e.changedBy,
        timestamp: e.timestamp ? new Date(e.timestamp) : null,

        oldValues: oldObj,
        newValues: newObj,

        changes,

        rawOldValues: e.oldValues,
        rawNewValues: e.newValues,

        entityName: e.entityName,
      };
    })
    .sort(
      (a, b) => (b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0)
    );
}

/** Legacy group-change rows stored OldGroupName + NewGroupName in newValues — fold into GroupName. */
function normalizeGroupChangeFields(
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
  eventName?: string,
) {
  if (eventName !== "SubscriptionParkerGroupChangedEvent") return;
  if ("GroupName" in newObj || "GroupName" in oldObj) return;
  if (!("OldGroupName" in newObj) && !("NewGroupName" in newObj)) return;

  oldObj.GroupName = newObj.OldGroupName ?? oldObj.OldGroupName ?? "Unassigned";
  newObj.GroupName = newObj.NewGroupName ?? "Unassigned";
  delete newObj.OldGroupName;
  delete newObj.NewGroupName;
  delete oldObj.OldGroupName;
  delete oldObj.NewGroupName;
}

function safeJson(x) {
  if (!x) return null;
  try {
    return JSON.parse(x);
  } catch {
    return x;
  }
}

export function formatAuditValue(value: any, field?: string): string {
  if (value === null || value === undefined) return "null";

  if (value === "") return "";

  if (field === "Credential" || field === "PaymentMethodLast4") {
    return String(value);
  }

  if (field === "ParkerName") {
    return String(value);
  }

  if (field === "Status") {
    return formatSubscriptionStatus(value);
  }

  if (typeof value === "object") {
    if (Object.keys(value).length === 0) return "null";

    const possibleDate = new Date(value);
    if (!isNaN(possibleDate.getTime())) {
      return toLocalDateTime(possibleDate.toISOString());
    }

    return JSON.stringify(value);
  }

  if (typeof value === "string") {
    // Avoid treating last4 / numeric ids as years (e.g. new Date("1110")).
    if (!/^\d{1,4}$/.test(value.trim())) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return toLocalDateTime(value);
      }
    }
  }

  return String(value);
}

export function isAddRemoveEvent(eventName: string) {
  return eventName.includes("Created") || eventName.includes("Removed") || eventName.includes("Deleted");
}

/**
 * Audit fields that should display only the new value (no "Previous" column).
 * Use for fields that are not stored historically (e.g. CancellationReason).
 */
export const AUDIT_FIELDS_NEW_VALUE_ONLY: readonly string[] = [
  "CancellationReason",
  "ParkerName",
];

/** Fields omitted from audit log UI (metadata, not meaningful to operators). */
export const AUDIT_FIELDS_HIDDEN: readonly string[] = [
  "UpdatedWhen",
  "ParkerId",
  "PlanPriceId",
  "PaymentMethodId",
  "ProcessorPaymentMethodKey",
  "OldGroupId",
  "NewGroupId",
  "OldGroupName",
  "NewGroupName",
];

export function isHiddenAuditField(field: string): boolean {
  return AUDIT_FIELDS_HIDDEN.includes(field);
}

export function isNewValueOnlyField(field: string): boolean {
  return AUDIT_FIELDS_NEW_VALUE_ONLY.includes(field);
}

const AUDIT_FIELD_LABELS: Record<string, string> = {
  GroupName: "Group",
  PlanPrice: "Price",
  PaymentMethodDescription: "Description",
  PaymentMethodLast4: "Last 4",
};

export function auditFieldLabel(field: string): string {
  return AUDIT_FIELD_LABELS[field] ?? field;
}
